import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import { BASE_URL } from "../../Utils/apiBase";
import { getSocialProviders, getSocialLogins, getSocialRegistration } from "../../Utils/authService";

const ERROR_CODES = new Set(["failed", "cancelled", "unavailable", "expired", "disabled", "locked", "confirm_email",
  "approval_pending", "email_required", "account_exists", "already_linked", "invalid_profile", "farm_required", "account_error"]);

export const socialErrorText = (t, code) => t(`social_auth.errors.${ERROR_CODES.has(code) ? code : "failed"}`, {
  defaultValue: "We could not complete sign-in. Please try again or use your email.",
});

export function SocialAuthNotice() {
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const code = params.get("socialError");
  if (!code) return null;
  return <Notice role="alert">{socialErrorText(t, code)}</Notice>;
}

export function useSocialSignup(enabled) {
  const [state, setState] = useState({ loading: enabled, profile: null, error: null });
  useEffect(() => {
    let active = true;
    if (!enabled) {
      setState({ loading: false, profile: null, error: null });
      return undefined;
    }
    setState({ loading: true, profile: null, error: null });
    getSocialRegistration().then((profile) => {
      if (active) setState({ loading: false, profile, error: null });
    }).catch(() => {
      if (active) setState({ loading: false, profile: null, error: "expired" });
    });
    return () => { active = false; };
  }, [enabled]);
  return state;
}

export function VerifiedSocialAccount({ profile }) {
  const { t } = useTranslation();
  return <Notice role="status">
    {t("social_auth.verified", { provider: profile.provider === "apple" ? "Apple" : "Google", email: profile.email,
      defaultValue: "{{email}} verified with {{provider}}. Complete your details below." })}
  </Notice>;
}

export default function SocialAuthButtons({ role, linkAccount = false }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [providers, setProviders] = useState(null);
  const [linked, setLinked] = useState([]);
  useEffect(() => {
    let active = true;
    getSocialProviders().then((result) => { if (active) setProviders(result); }).catch(() => {});
    if (linkAccount) getSocialLogins().then((result) => { if (active) setLinked(result); }).catch(() => {});
    return () => { active = false; };
  }, [linkAccount]);

  // Local design review only. Unset in every deployed build, so production still
  // shows a provider button exactly when that provider can actually complete.
  const preview = process.env.NODE_ENV !== "production" && process.env.REACT_APP_SOCIAL_PREVIEW === "1";
  const available = ["google", "apple"].filter(
    (provider) => preview || providers?.[provider] === true
  );
  if (!available.length) return null;
  const startUrl = (provider) => {
    const query = new URLSearchParams();
    if (role) query.set("role", role);
    if (linkAccount) query.set("intent", "link");
    const returnTo = params.get("returnTo");
    if (returnTo) query.set("returnTo", returnTo);
    return `${providers?.startBaseUrl ?? BASE_URL}/api/Auth/external/start/${provider}?${query}`;
  };

  return (
    <Group $linked={linkAccount} aria-label={t(linkAccount ? "social_auth.connected_accounts" : "social_auth.options", {
      defaultValue: linkAccount ? "Connected accounts" : "Other ways to sign in",
    })}>
      {linkAccount && <h2>{t("social_auth.connected_accounts", { defaultValue: "Connected accounts" })}</h2>}
      {linkAccount && <p>{t("social_auth.connect_hint", { defaultValue: "Connect Google or Apple to sign in to this same account. Your account type stays the same." })}</p>}
      {linkAccount && params.get("socialLinked") === "1" &&
        <Notice role="status">{t("social_auth.linked_success", { defaultValue: "Sign-in method connected." })}</Notice>}
      {available.map((provider) => {
        const name = provider === "apple" ? "Apple" : "Google";
        return linked.includes(provider)
          ? <Connected key={provider}>{name} <span>{t("social_auth.connected", { defaultValue: "Connected" })}</span></Connected>
          : <ProviderLink key={provider} href={startUrl(provider)} data-provider={provider} $provider={provider}>
              {provider === "apple" ? <FaApple aria-hidden="true" /> : <FcGoogle aria-hidden="true" />}
              {t(linkAccount ? "social_auth.connect" : "social_auth.continue", {
                provider: name, defaultValue: linkAccount ? "Connect {{provider}}" : "Continue with {{provider}}",
              })}
            </ProviderLink>;
      })}
      {!linkAccount && <Divider>{t("social_auth.or_email", { defaultValue: "or use your email" })}</Divider>}
      {linkAccount && <Link to="/forgot-password">{t("social_auth.set_password", { defaultValue: "Set or reset an email password" })}</Link>}
    </Group>
  );
}

const Group = styled.section`
  padding: ${({ theme, $linked }) => $linked ? theme.spacing(3) : 0};
  border-radius: ${({ theme }) => theme.radius.lg}px;
  background: ${({ theme, $linked }) => $linked ? theme.colors.background.surface : "transparent"};
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.25)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  h2 { margin: 0; font-size: ${({ theme }) => theme.typography.h3}; }
  p { margin: 0; color: ${({ theme }) => theme.colors.text.secondary}; }
  a:focus-visible { outline: 3px solid ${({ theme }) => theme.colors.border.focus}; outline-offset: 3px; }
`;
const ProviderLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-height: ${({ theme }) => theme.spacing(6.5)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid
    ${({ theme, $provider }) =>
      $provider === "apple" ? theme.colors.text.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.radius.pill}px;
  background: ${({ theme, $provider }) =>
    $provider === "apple" ? theme.colors.text.primary : theme.colors.background.surface};
  color: ${({ theme, $provider }) =>
    $provider === "apple" ? theme.colors.text.inverse : theme.colors.text.primary};
  text-decoration: none;
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.body};
  transition: opacity ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 22px;
    height: 22px;
    flex: 0 0 auto;
  }

  &:hover {
    background: ${({ theme, $provider }) =>
      $provider === "apple" ? theme.colors.text.primary : theme.colors.state.hover};
    opacity: ${({ $provider }) => ($provider === "apple" ? 0.88 : 1)};
  }

  &:active {
    opacity: 0.75;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const Connected = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.colors.text.primary};
  span { color: ${({ theme }) => theme.colors.text.secondary}; }
`;
const Divider = styled.div`
  display: flex; align-items: center; gap: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.small};
  margin-block: ${({ theme }) => theme.spacing(1)};
  &::before, &::after { content: ""; height: 1px; flex: 1; background: ${({ theme }) => theme.colors.border.subtle}; }
`;
const Notice = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.5)};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${({ theme }) => theme.radius.md}px;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;
